import { 
  SearchOutlined, 
  ReloadOutlined, 
  DownloadOutlined,
  BankOutlined,
  DollarOutlined,
  SwapOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { 
  Card, 
  Table, 
  Button, 
  DatePicker, 
  Input, 
  Select, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Statistic, 
  Tag, 
  message,
  Spin,
  Empty,
  Tooltip
} from 'antd';
import dayjs from 'dayjs';
import React, { useState, useEffect, useCallback } from 'react';

import { bankApi } from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Banka hesabı arayüzü
interface BankAccount {
  bankAccountCode: string;
  bankAccountDescription: string;
  currencyCode: string;
  iban?: string;
  bankAccountNumber?: string;
}

// Banka hareketi arayüzü
interface BankTransaction {
  bankAccountCode: string;
  bankAccountDescription: string;
  documentDate: string;
  documentTime: string;
  documentNumber: string;
  bankTransTypeCode: string;
  bankTransTypeDescription: string;
  bankTransNumber: string;
  currAccCode: string;
  currAccDescription: string;
  description: string;
  loc_CurrencyCode: string;
  loc_Debit: number;
  loc_Credit: number;
  loc_Balance: number;
  doc_CurrencyCode: string;
  doc_Debit: number;
  doc_Credit: number;
  doc_Balance: number;
  createdDate: string;
  currAccTypeCode: string;
  currAccTypeDescription: string;
  lineDescription: string;
  refNumber: string;
  glTypeCode: string;
  sumDescription: string;
  applicationCode: string;
  applicationDescription: string;
  glAccCode: string;
  glAccDescription: string;
  chequeTransTypeDescription: string;
  loc_ExchangeRate: string;
  isTurnover: number;
  isPostingJournal: number;
  journalDate: string;
  journalNumber: string;
  importFileNumber: string;
  exportFileNumber: string;
  lastUpdatedUserName: string;
  lastUpdatedDate: string;
  linkedApplicationCode: string;
  linkedApplicationID: string;
  bankBranchCode: string;
  bankBranchDescription: string;
  bankAccountNumber: string;
  iban: string;
  valueDate: string;
  processingDate: string;
  checkNumber: string;
  checkDate: string;
  checkBank: string;
  checkBranch: string;
  transactionCode: string;
  transactionDescription: string;
  referenceNumber: string;
  additionalInfo: string;
  exchangeRate: number;
  bankStatementLine: string;
  reconciliationStatus: string;
  reconciliationDate: string;
  reconciliationUser: string;

}

// Banka özeti arayüzü
interface BankSummary {
  bankAccountCode: string;
  bankAccountDescription: string;
  currencyCode: string;
  openingBalance: number;
  totalReceipts: number;
  totalPayments: number;
  totalIncomingTransfers: number;
  totalOutgoingTransfers: number;
  closingBalance: number;
  balance: number;
  bankBranchCode: string;
  bankBranchDescription: string;
  bankAccountNumber: string;
  iban: string;
  bankCode: string;
  bankName: string;
  lastTransactionDate: string;
  transactionCount: number;
}

const BankTransactionsPage: React.FC = () => {
  // State tanımlamaları
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [bankSummary, setBankSummary] = useState<BankSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  // Filtreleme state'leri
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [searchText, setSearchText] = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState<string>('');
  const [selectedTransactionType, setSelectedTransactionType] = useState<string>('');
  
  // Sayfalama state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Fiş tipleri
  const transactionTypes = [
    { value: '1', label: 'Gelen Havale' },
    { value: '2', label: 'Giden Havale' },
    { value: '3', label: 'Çek' },
    { value: '4', label: 'Senet' },
    { value: '5', label: 'Kredi Kartı' },
    { value: '6', label: 'Nakit' },
    { value: '7', label: 'Diğer' }
  ];

  // Banka hesaplarını yükle
  const loadBankAccounts = useCallback(async () => {
    try {
      const accounts = await bankApi.getBankAccounts();
      setBankAccounts(accounts || []);
    } catch (error) {
      console.error('Banka hesapları yüklenirken hata:', error);
      setBankAccounts([]);
    }
  }, []);

  // Banka özetini yükle
  const loadBankSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      if (selectedBankAccount) {
        const response = await bankApi.getBankSummary(selectedBankAccount);
        if (response.success && response.data) {
          setBankSummary(Array.isArray(response.data) ? response.data : [response.data]);
        }
      } else {
        // Tüm banka hesapları için özet bilgilerini al
        const accounts = await bankApi.getBankAccounts();
        const summaries = [];
        for (const account of accounts) {
          try {
            const response = await bankApi.getBankSummary(account.bankAccountCode);
            if (response.success && response.data) {
              summaries.push(response.data);
            }
          } catch (error) {
            console.error(`${account.bankAccountCode} için özet alınamadı:`, error);
          }
        }
        setBankSummary(summaries);
      }
    } catch (error) {
      console.error('Banka özeti yüklenirken hata:', error);
      message.error('Banka özeti yüklenirken hata oluştu');
    } finally {
      setSummaryLoading(false);
    }
  }, [selectedBankAccount]);

  // Banka hareketlerini yükle
  const loadBankTransactions = useCallback(async (page: number = 1, append: boolean = false) => {
    setLoading(true);
    try {
      const params: any = {
        page,
        pageSize,
      };

      // Tarih aralığı parametreleri
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format('YYYYMMDD');
        params.endDate = dateRange[1].format('YYYYMMDD');
      }

      // Diğer filtreler
      if (searchText.trim()) {
        params.searchText = searchText.trim();
      }
      if (selectedBankAccount) {
        params.bankAccountCode = selectedBankAccount;
      }
      if (selectedTransactionType) {
        params.bankTransTypeCode = selectedTransactionType;
      }

      const response = await bankApi.getBankTransactionReport(params);
      
      if (response.success && response.data) {
        const newTransactions = response.data.map((transaction: BankTransaction) => ({
          ...transaction,
          bankAccountCode: transaction.bankAccountCode || transaction.bankAccountCode
        }));

        if (append) {
          setTransactions(prev => [...prev, ...newTransactions]);
        } else {
          setTransactions(newTransactions);
        }

        setTotalRecords(response.totalCount || 0);
        setHasMore(newTransactions.length === pageSize);
        setCurrentPage(page);
      } else {
        console.error('API yanıtı başarısız:', response);
        message.error(response.message || 'Banka hareketleri alınamadı');
      }
    } catch (error) {
      console.error('Banka hareketleri yüklenirken hata:', error);
      message.error('Banka hareketleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [dateRange, searchText, selectedBankAccount, selectedTransactionType, pageSize]);

  // Sayfa yüklendiğinde çalışacak
  useEffect(() => {
    loadBankAccounts();
    loadBankSummary();
    loadBankTransactions();
  }, [loadBankAccounts, loadBankSummary, loadBankTransactions]);

  // Filtreleme işlemleri
  const handleSearch = () => {
    setCurrentPage(1);
    loadBankTransactions(1, false);
    loadBankSummary();
  };

  const handleReset = () => {
    setSearchText('');
    setSelectedBankAccount('');
    setSelectedTransactionType('');
    setDateRange([dayjs().subtract(30, 'day'), dayjs()]);
    setCurrentPage(1);
    setTimeout(() => {
      loadBankTransactions(1, false);
      loadBankSummary();
    }, 100);
  };

  // Daha fazla yükle (infinite scroll)
  const loadMore = () => {
    if (hasMore && !loading) {
      loadBankTransactions(currentPage + 1, true);
    }
  };

  // Tablo sütunları
  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'documentDate',
      key: 'documentDate',
      width: 100,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
      sorter: true,
    },
    {
      title: 'Belge No',
      dataIndex: 'documentNumber',
      key: 'documentNumber',
      width: 120,
    },
    {
      title: 'Banka Hesabı',
      dataIndex: 'bankAccountDescription',
      key: 'bankAccountDescription',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'İşlem Tipi',
      dataIndex: 'bankTransTypeDescription',
      key: 'bankTransTypeDescription',
      width: 120,
      render: (type: string) => (
        <Tag color={type?.includes('Gelen') ? 'green' : type?.includes('Giden') ? 'red' : 'blue'}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Para Birimi',
      dataIndex: 'loc_CurrencyCode',
      key: 'loc_CurrencyCode',
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Borç',
      dataIndex: 'loc_Debit',
      key: 'loc_Debit',
      width: 120,
      align: 'right' as const,
      render: (amount: number) => amount > 0 ? 
        <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>
          {amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text> : '-',
    },
    {
      title: 'Alacak',
      dataIndex: 'loc_Credit',
      key: 'loc_Credit',
      width: 120,
      align: 'right' as const,
      render: (amount: number) => amount > 0 ? 
        <Text style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          {amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text> : '-',
    },
    {
      title: 'IBAN',
      dataIndex: 'iban',
      key: 'iban',
      width: 200,
      ellipsis: true,
      render: (iban: string) => iban ? (
        <Tooltip title={iban}>
          <Text code>{iban}</Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: 'Referans No',
      dataIndex: 'refNumber',
      key: 'refNumber',
      width: 120,
      ellipsis: true,
    },
  ];

  // Özet kartları
  const renderSummaryCards = () => {
    if (summaryLoading) {
      return (
        <Row gutter={[16, 16]}>
          {[1, 2, 3, 4].map(i => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Card>
                <Spin />
              </Card>
            </Col>
          ))}
        </Row>
      );
    }

    if (!bankSummary || bankSummary.length === 0) {
      return (
        <Card>
          <Empty description="Özet bilgisi bulunamadı" />
        </Card>
      );
    }

    // Tüm hesapların toplamını hesapla
    const totalSummary = bankSummary.reduce((acc, summary) => ({
      totalReceipts: acc.totalReceipts + (summary.totalReceipts || 0),
      totalPayments: acc.totalPayments + (summary.totalPayments || 0),
      closingBalance: acc.closingBalance + (summary.closingBalance || 0),
      transactionCount: acc.transactionCount + (summary.transactionCount || 0),
    }), {
      totalReceipts: 0,
      totalPayments: 0,
      closingBalance: 0,
      transactionCount: 0,
    });

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Toplam Gelen"
              value={totalSummary.totalReceipts}
              precision={2}
              valueStyle={{ color: '#3f8600' }}
              prefix={<DollarOutlined />}
              suffix="TL"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Toplam Giden"
              value={totalSummary.totalPayments}
              precision={2}
              valueStyle={{ color: '#cf1322' }}
              prefix={<SwapOutlined />}
              suffix="TL"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Net Bakiye"
              value={totalSummary.closingBalance}
              precision={2}
              valueStyle={{ color: totalSummary.closingBalance >= 0 ? '#3f8600' : '#cf1322' }}
              prefix={<BankOutlined />}
              suffix="TL"
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Toplam İşlem"
              value={totalSummary.transactionCount}
              valueStyle={{ color: '#1890ff' }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <BankOutlined /> Banka Hareketleri
      </Title>

      {/* Özet Kartları */}
      <div style={{ marginBottom: '24px' }}>
        {renderSummaryCards()}
      </div>

      {/* Filtreler */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <label>Tarih Aralığı:</label>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
              format="DD.MM.YYYY"
              style={{ width: '100%' }}
              placeholder={['Başlangıç', 'Bitiş']}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <label>Arama:</label>
            <Input
              placeholder="Belge no, açıklama ara..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <label>Banka Hesabı:</label>
            <Select
              placeholder="Tüm hesaplar"
              value={selectedBankAccount}
              onChange={setSelectedBankAccount}
              style={{ width: '100%' }}
              allowClear
            >
              {bankAccounts
                .filter(account => account && account.bankAccountCode) // null değerleri filtrele
                .map(account => (
                  <Option key={account.bankAccountCode} value={account.bankAccountCode}>
                    {account.bankAccountDescription} ({account.currencyCode})
                  </Option>
                ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <label>İşlem Tipi:</label>
            <Select
              placeholder="Tüm işlemler"
              value={selectedTransactionType}
              onChange={setSelectedTransactionType}
              style={{ width: '100%' }}
              allowClear
            >
              {transactionTypes
                .filter(type => type && type.value) // null değerleri filtrele
                .map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ paddingTop: '24px' }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  Ara
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Temizle
                </Button>
                <Button icon={<DownloadOutlined />}>
                  Excel
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Tablo */}
      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>
            Toplam {totalRecords.toLocaleString()} kayıt bulundu
          </Text>
          <Text type="secondary">
            Sayfa {currentPage} - {pageSize} kayıt gösteriliyor
          </Text>
        </div>

        <Table
          columns={columns}
          dataSource={transactions}
          rowKey={(record) => `${record.documentNumber}-${record.bankTransNumber}`}
          loading={loading}
          pagination={false}
          scroll={{ x: 1400, y: 600 }}
          size="small"
        />

        {/* Daha fazla yükle butonu */}
        {hasMore && transactions.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Button 
              type="primary" 
              loading={loading} 
              onClick={loadMore}
              size="large"
            >
              Daha Fazla Yükle ({totalRecords - transactions.length} kaldı)
            </Button>
          </div>
        )}

        {/* Veri yoksa */}
        {!loading && transactions.length === 0 && (
          <Empty 
            description="Banka hareketi bulunamadı" 
            style={{ margin: '40px 0' }}
          />
        )}
      </Card>
    </div>
  );
};

export default BankTransactionsPage;